import React, { useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Chip,
  Paper,
  Stack,
  Select,
  MenuItem,
  useTheme,
  TablePagination,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Person,
  Business,
  Visibility,
  ElectricBolt,
  SolarPower,
  EvStation,
  SwapHoriz,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import { CLIENT_TYPES, CLIENT_TAGS, CLIENT_TAG_LABELS } from "../../utils/models/clientModel";

const ClientList = ({ onView, onEdit, filter = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: clients, loading } = useCollection("clients");
  const [localClients, setLocalClients] = useState(null);
  const { remove } = useAuditFirestore("clients");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [clientTypeFilter, setClientTypeFilter] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  React.useEffect(() => {
    if (clients) {
      setLocalClients(clients);
    }
  }, [clients]);

  React.useEffect(() => {
    setPage(0);
  }, [search, clientTypeFilter, selectedTags]);

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((t) => t !== tag)
        : [...prev, tag]
    );
  };

  const filtered = localClients?.filter(
    (c) =>
      c.isActive !== false &&
      (clientTypeFilter === "" || c.type === clientTypeFilter) &&
      (selectedTags.length === 0 ||
        selectedTags.every((tag) => c.tags?.includes(tag))) &&
      (search === "" ||
        `${c.firstName || ""} ${c.lastName || ""} ${c.companyName || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.phone?.toLowerCase().includes(search.toLowerCase())),
  );

  const paginatedClients = filtered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getInitials = (client) => {
    if (client.firstName && client.lastName)
      return `${client.firstName[0]}${client.lastName[0]}`;
    if (client.companyName)
      return client.companyName.substring(0, 2).toUpperCase();
    return "?";
  };

  const getDisplayName = (client) => {
    if (client.type === CLIENT_TYPES.COMPANY) {
      return client.companyName || "Sin nombre";
    }
    return (
      `${client.firstName || ""} ${client.lastName || ""}`.trim() ||
      "Sin nombre"
    );
  };

  const getTagIcon = (tag) => {
    switch (tag) {
      case CLIENT_TAGS.CAMBIO_COMERCIALIZADORA:
        return <SwapHoriz sx={{ fontSize: 14 }} />;
      case CLIENT_TAGS.FOTOVOLTAICA:
        return <SolarPower sx={{ fontSize: 14 }} />;
      case CLIENT_TAGS.CARGADOR_ELECTRICO:
        return <EvStation sx={{ fontSize: 14 }} />;
      default:
        return null;
    }
  };

  const getTagColor = (tag) => {
    switch (tag) {
      case CLIENT_TAGS.CAMBIO_COMERCIALIZADORA:
        return "warning";
      case CLIENT_TAGS.FOTOVOLTAICA:
        return "success";
      case CLIENT_TAGS.CARGADOR_ELECTRICO:
        return "info";
      default:
        return "default";
    }
  };

  if (loading || !localClients) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1.5}
            flexWrap="wrap"
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flex={1} flexWrap="wrap">
              {/* SEARCH */}
              <TextField
                placeholder="Buscar por nombre o email"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: 280, md: 360 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: theme.palette.background.paper,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        ✖
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* FILTRO TIPO */}
              <Select
                size="small"
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e.target.value)}
                displayEmpty
                sx={{
                  width: { xs: "100%", sm: 160, md: 180 },
                  height: 44,
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.divider,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={CLIENT_TYPES.INDIVIDUAL}>Particular</MenuItem>
                <MenuItem value={CLIENT_TYPES.COMPANY}>Empresa</MenuItem>
              </Select>
            </Stack>

            {/* TOTAL */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`Total: ${filtered?.length || 0}`}
                sx={{
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  background: theme.palette.background.paper,
                  border: "1px solid",
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                }}
              />

              <Tooltip title="Refrescar">
                <IconButton
                  onClick={() => {
                    if (clients) setLocalClients(clients);
                  }}
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 175, 163, 0.1)"
                          : "rgba(0, 95, 101, 0.05)",
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* FILTRO DE TAGS */}
          <FormGroup row sx={{ gap: 1, flexWrap: "wrap" }}>
            {Object.entries(CLIENT_TAG_LABELS).map(([value, label]) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    checked={selectedTags.includes(value)}
                    onChange={() => handleTagToggle(value)}
                    size="small"
                    icon={getTagIcon(value)}
                    checkedIcon={getTagIcon(value)}
                    sx={{
                      color: theme.palette.text.secondary,
                      "&.Mui-checked": {
                        color: theme.palette[getTagColor(value)]?.main || theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                    {label}
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
            ))}
          </FormGroup>
        </Stack>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState
          title={search || selectedTags.length > 0 ? "Sin resultados" : "Sin clientes"}
          description={
            search || selectedTags.length > 0 ? "Prueba con otros términos" : "Añade tu primer cliente"
          }
          actionLabel="Nuevo Cliente"
          onAction={() => onEdit(null)}
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: { xs: 50, sm: 60 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {isMobile ? "" : "Avatar"}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Cliente
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Teléfono
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Etiquetas
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: { xs: 100, sm: 130 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedClients?.map((client) => (
                  <TableRow
                    key={client.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0, 95, 101, 0.02)",
                      },
                    }}
                  >
                    <TableCell onClick={() => onView(client)}>
                      <Avatar
                        sx={{
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          background:
                            client.type === CLIENT_TYPES.COMPANY
                              ? `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`
                              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          fontSize: { xs: "0.8rem", sm: "1rem" },
                          fontWeight: 500,
                        }}
                      >
                        {client.type === CLIENT_TYPES.COMPANY ? (
                          <Business fontSize="small" />
                        ) : (
                          getInitials(client)
                        )}
                      </Avatar>
                    </TableCell>
                    <TableCell onClick={() => onView(client)}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {getDisplayName(client)}
                      </Typography>
                      {isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {client.email || "—"}
                        </Typography>
                      )}
                      {client.type === CLIENT_TYPES.COMPANY &&
                        client.firstName && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                            {client.firstName} {client.lastName}
                          </Typography>
                        )}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell onClick={() => onView(client)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {client.email || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={() => onView(client)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {client.phone || "—"}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell onClick={() => onView(client)}>
                      <Chip
                        size="small"
                        icon={
                          client.type === CLIENT_TYPES.COMPANY ? (
                            <Business sx={{ fontSize: { xs: 12, sm: 14 } }} />
                          ) : (
                            <Person sx={{ fontSize: { xs: 12, sm: 14 } }} />
                          )
                        }
                        label={
                          client.type === CLIENT_TYPES.COMPANY
                            ? "Empresa"
                            : "Particular"
                        }
                        color={
                          client.type === CLIENT_TYPES.COMPANY
                            ? "secondary"
                            : "primary"
                        }
                        variant="outlined"
                        sx={{
                          minWidth: { xs: 70, sm: 85 },
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          "& .MuiChip-icon": { fontSize: { xs: 12, sm: 14 }, ml: 0.5 },
                        }}
                      />
                    </TableCell>
                    <TableCell onClick={() => onView(client)}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {client.tags?.includes(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA) && (
                          <Tooltip title="Cambio de comercializadora">
                            <Chip
                              icon={<SwapHoriz sx={{ fontSize: 14 }} />}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                            />
                          </Tooltip>
                        )}
                        {client.tags?.includes(CLIENT_TAGS.FOTOVOLTAICA) && (
                          <Tooltip title="Fotovoltaica">
                            <Chip
                              icon={<SolarPower sx={{ fontSize: 14 }} />}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                            />
                          </Tooltip>
                        )}
                        {client.tags?.includes(CLIENT_TAGS.CARGADOR_ELECTRICO) && (
                          <Tooltip title="Cargador eléctrico">
                            <Chip
                              icon={<EvStation sx={{ fontSize: 14 }} />}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                            />
                          </Tooltip>
                        )}
                        {(!client.tags || client.tags.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            —
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(client);
                            }}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(client);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(client);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginación */}
          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[7, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: { xs: "0.7rem", sm: "0.85rem" },
              },
            }}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalClients((prev) =>
            prev.filter((c) => c.id !== deleteTarget.id),
          );
          const deletedClient = deleteTarget;
          setDeleteTarget(null);

          try {
            await remove(deletedClient.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalClients((prev) => [...prev, deletedClient]);
          }
        }}
        title="Eliminar Cliente"
        message={`¿Eliminar a ${getDisplayName(deleteTarget || {})}?`}
        type="delete"
      />
    </Box>
  );
};

export default ClientList;